import React, { useEffect, useState, useMemo } from 'react';
import { ForumThread, ForumReply, UserRole } from '../types';
import {
  MessageSquare,
  Users,
  Eye,
  Clock,
  Plus,
  Search,
  X,
  ShieldAlert,
  MoreVertical,
  Edit,
  Trash2,
  ArrowLeft,
} from 'lucide-react';
import { 
  checkContentForViolation,
  MODERATION_WARNING_TEXT
} from '../services/moderationService';
import { useToast } from '../contexts/ToastContext';
import { useCanManage } from '@/hooks/usePermissions';
import { useData } from '@/contexts/DataContext';
import { API_BASE_URL } from '@/config';

const getMockThreads = (): Omit<ForumThread, 'author' | 'authorAvatar'>[] => [
  {
    id: '1',
    authorId: 'user-1',
    title: 'Looking for a bassist for a synthwave project in Brooklyn',
    category: 'Collaboration',
    replies: 3,
    views: 342,
    lastActive: '2h ago',
    isPinned: true,
  },
  {
    id: '2',
    authorId: 'user-2',
    title: 'Selling my Moog Sub 37 - Mint Condition',
    category: 'Gear Swap',
    replies: 1,
    views: 120,
    lastActive: '4h ago',
  },
  {
    id: '3',
    authorId: 'user-2',
    title: 'Tips for efficient DAO proposal drafting?',
    category: 'General',
    replies: 1,
    views: 890,
    lastActive: '1d ago',
  },
  {
    id: '4',
    authorId: 'u_artist',
    title: 'My latest visual set for The Warehouse (Showcase)',
    category: 'Showcase',
    replies: 0,
    views: 1500,
    lastActive: '1d ago',
  },
];

const getMockReplies = (threadId: string): Omit<ForumReply, 'author' | 'authorAvatar'>[] => {
    const allReplies = [
        { id: 'rep-1', threadId: '1', authorId: 'user-2', content: 'Hey, I play bass. What are your influences?', createdAt: '2023-10-27T10:00:00Z', updatedAt: '2023-10-27T10:00:00Z' },
        { id: 'rep-2', threadId: '1', authorId: 'user-1', content: 'Awesome! Stuff like The Midnight, Com Truise, PYLOT.', createdAt: '2023-10-27T10:05:00Z', updatedAt: '2023-10-27T10:05:00Z' },
        { id: 'rep-3', threadId: '1', authorId: 'user-2', content: 'Cool, I\'m into that. Got any demos?', createdAt: '2023-10-27T10:10:00Z', updatedAt: '2023-10-27T10:10:00Z' },
        { id: 'rep-4', threadId: '2', authorId: 'u_artist', content: 'Is it still available? DM me.', createdAt: '2023-10-27T11:00:00Z', updatedAt: '2023-10-27T11:00:00Z' },
        { id: 'rep-5', threadId: '3', authorId: 'user-1', content: 'Start with a clear problem statement and proposed solution. Keep it concise.', createdAt: '2023-10-26T14:00:00Z', updatedAt: '2023-10-26T14:00:00Z' },
    ];
    return allReplies.filter(r => r.threadId === threadId);
}

const ThreadReplies: React.FC<{ thread: ForumThread, replies: ForumReply[], onReply: (newReply: ForumReply) => void, onBlockUser: () => void, currentUserId: string }> = ({ thread, replies, onReply, onBlockUser, currentUserId }) => {
  const [newReplyContent, setNewReplyContent] = useState('');
  const { notify } = useToast();
  const { getUserById } = useData();

  const handlePostReply = async () => {
    if (!newReplyContent.trim()) {
      notify('Reply content cannot be empty.', 'warn');
      return;
    }

    if (checkContentForViolation(newReplyContent)) {
      notify('Reply blocked due to forbidden language. Your account has been flagged.', 'error');
      onBlockUser();
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/forum-threads/${thread.id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newReplyContent, authorId: currentUserId }),
      });

      if (!response.ok) {
        throw new Error('Failed to post reply.');
      }

      const createdReply: ForumReply = await response.json();
      const author = getUserById(createdReply.authorId);
      onReply({
        ...createdReply,
        author: author?.name || 'You',
        authorAvatar: author?.avatar || 'https://picsum.photos/seed/placeholder/50',
      });
      setNewReplyContent('');
      notify('Reply posted successfully!', 'success');
    } catch (error) {
      console.error(error);
      notify('Could not post reply.', 'error');
    }
  };

  return (
    <div className="mt-6">
      <h4 className="text-lg font-bold text-white mb-4">Replies</h4>
      <div className="space-y-4">
        {replies.map((reply) => {
          const author = getUserById(reply.authorId);
          return (
            <div key={reply.id} className="bg-kala-800/40 p-3 rounded-lg border border-kala-700">
              <div className="flex items-start gap-3">
                <img src={author?.avatar || 'https://picsum.photos/seed/placeholder/50'} className="w-8 h-8 rounded-full" alt={author?.name} />
                <div>
                  <p className="text-sm text-kala-200">{reply.content}</p>
                  <div className="text-xs text-kala-400 mt-1">
                    <span>{author?.name || 'Unknown User'}</span> &middot; <span>{new Date(reply.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6">
        <textarea
          rows={3}
          value={newReplyContent}
          onChange={(e) => setNewReplyContent(e.target.value)}
          className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary resize-none"
          placeholder="Add your reply..."
        />
        <button
          onClick={handlePostReply}
          className="mt-2 bg-kala-secondary text-kala-900 font-bold px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors"
        >
          Post Reply
        </button>
      </div>
    </div>
  );
};

const ForumThreadDetail: React.FC<{
  thread: ForumThread & { content?: string };
  onBack: () => void;
  onBlockUser: () => void;
  currentUserId: string;
  currentUserRole: UserRole;
}> = ({ thread, onBack, onBlockUser, currentUserId, currentUserRole }) => {
  const [replies, setReplies] = useState<ForumReply[]>([]);
  const { getUserById } = useData();

  useEffect(() => {
    const enrichReply = (reply: Omit<ForumReply, 'author' | 'authorAvatar'>): ForumReply => {
        const author = getUserById(reply.authorId);
        return {
            ...reply,
            author: author?.name || 'Unknown User',
            authorAvatar: author?.avatar || 'https://picsum.photos/seed/placeholder/50',
        } as ForumReply;
    };

    const fetchReplies = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/forum-threads/${thread.id}/replies`, { credentials: 'include' });
        if (!response.ok) throw new Error('Could not fetch replies.');
        const apiReplies: ForumReply[] = await response.json();
        if (apiReplies && apiReplies.length > 0) {
            setReplies(apiReplies.map(enrichReply));
        } else {
             // If API returns no replies, use mock ones for mock threads
            setReplies(getMockReplies(thread.id).map(enrichReply));
        }
      } catch (error) {
        console.error(error);
        // On error, use mock replies
        setReplies(getMockReplies(thread.id).map(enrichReply));
      }
    };
    if (thread.id && getUserById) fetchReplies();
  }, [thread.id, getUserById]);

  const handleAddReply = (newReply: ForumReply) => {
    setReplies([...replies, newReply]);
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-kala-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Forum
      </button>
      <div className="bg-kala-800/50 border border-kala-700 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-white mb-2">{thread.title}</h2>
        <div className="flex items-center gap-3 text-sm text-kala-400 mb-4">
          <span className="flex items-center gap-1.5">
            <img src={thread.authorAvatar} className="w-5 h-5 rounded-full" alt={thread.author} />
            {thread.author}
          </span>
          &middot;
          <span>{new Date(thread.lastActive).toLocaleString()}</span>
        </div>
        <p className="text-kala-300 whitespace-pre-wrap">{thread.content || "No content available."}</p>
        <ThreadReplies thread={thread} replies={replies} onReply={handleAddReply} onBlockUser={onBlockUser} currentUserId={currentUserId} />
      </div>
    </div>
  );
};


const ForumThreadRow: React.FC<{
  thread: ForumThread;
  currentUserId: string;
  currentUserRole: UserRole;
  onEdit: (thread: ForumThread) => void;
  onDelete: (threadId: string) => void;
  onSelect: (thread: ForumThread) => void;
  activeDropdown: string | null;
  setActiveDropdown: (id: string | null) => void;
}> = ({
  thread,
  currentUserId,
  currentUserRole,
  onEdit,
  onDelete,
  onSelect,
  activeDropdown,
  setActiveDropdown,
}) => {
  const { getUserById } = useData();
  const canManage = useCanManage(thread.authorId, currentUserId, currentUserRole);
  const author = useMemo(() => getUserById(thread.authorId), [thread.authorId, getUserById]);

  return (
    <div className="bg-kala-800/50 border border-kala-700 rounded-xl p-4 hover:border-kala-500 transition-colors group relative">
       <div onClick={() => onSelect(thread)} className="cursor-pointer">
        <div className="flex justify-between items-start gap-4">
          <div className="flex gap-4">
            <div className="flex flex-col items-center gap-1 min-w-[40px]">
              <MessageSquare className="w-5 h-5 text-kala-500 group-hover:text-kala-secondary transition-colors" />
              <span className="text-xs text-kala-400 font-mono">
                {thread.replies}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white group-hover:text-kala-secondary transition-colors mb-1">
                {thread.isPinned && (
                  <span className="text-yellow-400 mr-2 text-xs border border-yellow-500/30 bg-yellow-500/10 px-1.5 rounded">
                    PINNED
                  </span>
                )}
                {thread.title}
              </h3>
              <div className="flex items-center gap-3 text-xs text-kala-400">
                <span
                  className={`px-2 py-0.5 rounded border ${
                    thread.category === 'Collaboration'
                      ? 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                      : thread.category === 'Gear Swap'
                        ? 'bg-orange-500/10 border-orange-500/20 text-orange-400'
                        : 'bg-kala-700 border-kala-600'
                  }`}
                >
                  {thread.category}
                </span>
                <span className="flex items-center gap-1">
                  <img
                    src={thread.authorAvatar || 'https://picsum.photos/seed/placeholder/50'}
                    className="w-4 h-4 rounded-full"
                    alt={author?.name}
                  />
                  {thread.author || 'Unknown User'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {thread.lastActive}
                </span>
              </div>
            </div>
          </div>
  
          <div className="hidden md:flex flex-col items-end text-xs text-kala-500">
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" /> {thread.views}
            </div>
          </div>
        </div>
      </div>
      {canManage && (
        <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <button
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === thread.id ? null : thread.id
                )
              }
              className="p-1.5 bg-kala-900/50 rounded-full text-kala-400 hover:bg-kala-700 transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {activeDropdown === thread.id && (
              <div className="absolute right-0 mt-2 w-36 bg-kala-800 border border-kala-700 rounded-md shadow-lg z-10 py-1">
                <button
                  onClick={() => onEdit(thread)}
                  className="w-full text-left px-3 py-1.5 text-sm text-kala-300 hover:bg-kala-700 flex items-center gap-2"
                >
                  <Edit className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => onDelete(thread.id)}
                  className="w-full text-left px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const Forum: React.FC<{
  onBlockUser: () => void;
  currentUserRole: UserRole;
  currentUserId: string;
}> = ({ onBlockUser, currentUserRole, currentUserId }) => {
  const { notify } = useToast();
  const { getUserById } = useData();
  const [filter, setFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('General');
  const [newContent, setNewContent] = useState('');
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [editingThread, setEditingThread] = useState<ForumThread | null>(null);
  const [selectedThread, setSelectedThread] = useState<(ForumThread & { content?: string }) | null>(null);

  const filters = ['All', 'Collaboration', 'Gear Swap', 'General', 'Showcase'];

  useEffect(() => {
    const enrich = (thread: Omit<ForumThread, 'author' | 'authorAvatar'>): ForumThread => {
      const author = getUserById(thread.authorId);
      return {
        ...thread,
        author: author?.name || 'Unknown User',
        authorAvatar: author?.avatar || 'https://picsum.photos/seed/placeholder/50',
      } as ForumThread;
    };

    const fetchThreads = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/forum-threads`, { credentials: 'include' });
        if (!response.ok) throw new Error('Could not fetch forum threads.');
        const apiThreads: ForumThread[] = await response.json();
        if (Array.isArray(apiThreads) && apiThreads.length > 0) {
          const normalized = apiThreads.map((thread) => ({
            ...enrich(thread),
            lastActive: thread.lastActive ? new Date(thread.lastActive).toLocaleString() : 'Just now',
          }));
          setThreads(normalized);
        } else {
          setThreads(getMockThreads().map(enrich));
        }
      } catch (error) {
        console.error(error);
        setThreads(getMockThreads().map(enrich));
      }
    };
    if (getUserById) fetchThreads();
  }, [currentUserId, getUserById]);
  
  const handleCreateOrUpdateTopic = async () => {
    if (checkContentForViolation(`${newTitle} ${newContent}`)) {
      onBlockUser();
      setIsModalOpen(false);
      return;
    }

    if (!newTitle || !newContent) return;

    const url = editingThread
      ? `${API_BASE_URL}/api/forum-threads/${editingThread.id}`
      : `${API_BASE_URL}/api/forum-threads`;

    const method = editingThread ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newTitle,
          category: newCategory,
          content: newContent,
          authorId: editingThread ? undefined : currentUserId,
        }),
      });
      if (!response.ok) throw new Error(`Failed to ${editingThread ? 'update' : 'create'} thread.`);
      
      const savedThread: ForumThread = await response.json();
      const author = getUserById(savedThread.authorId);
      const enrichedThread = {
        ...savedThread,
        author: author?.name || "You",
        authorAvatar: author?.avatar || 'https://picsum.photos/seed/placeholder/50',
        lastActive: savedThread.lastActive ? new Date(savedThread.lastActive).toLocaleString() : 'Just now',
      };

      if (editingThread) {
        setThreads(threads.map(t => t.id === editingThread.id ? enrichedThread : t));
        notify('Topic updated successfully!', 'success');
      } else {
        setThreads([enrichedThread, ...threads]);
        notify('Topic posted successfully!', 'success');
      }
    } catch (error) {
      console.error(error);
      notify(`Error: ${error.message}`, 'error');
    }

    setIsModalOpen(false);
    setNewTitle('');
    setNewContent('');
    setEditingThread(null);
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forum-threads/${threadId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete thread.');
      setThreads(threads.filter(t => t.id !== threadId));
      notify(`Thread deleted.`, 'success');
    } catch (error) {
      console.error(error);
      notify('Failed to delete thread.', 'error');
    }
    setActiveDropdown(null);
  };
  
  const handleEditThread = (thread: ForumThread) => {
    setEditingThread(thread);
    setNewTitle(thread.title);
    setNewCategory(thread.category);
    setNewContent("Loading content..."); 
    setIsModalOpen(true);
    setActiveDropdown(null);

    fetch(`${API_BASE_URL}/api/forum-threads/${thread.id}`)
      .then(res => res.json())
      .then(data => setNewContent(data.content || ''))
      .catch(() => setNewContent('Could not load content.'));
  };

  const handleSelectThread = async (thread: ForumThread) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forum-threads/${thread.id}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Could not fetch thread details.');
      const threadDetails = await response.json();
      setSelectedThread({ ...thread, ...threadDetails });
    } catch (error) {
      console.error(error);
      notify('Could not load thread details.', 'error');
      setSelectedThread(thread); // Fallback to basic info
    }
  };

  const filteredThreads = useMemo(() => {
    return threads.filter((t) => filter === 'All' || t.category === filter);
  }, [threads, filter]);

  if (selectedThread) {
    return <ForumThreadDetail thread={selectedThread} onBack={() => setSelectedThread(null)} onBlockUser={onBlockUser} currentUserId={currentUserId} currentUserRole={currentUserRole} />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="text-kala-secondary" /> Community Forum
          </h2>
          <p className="text-kala-400 text-sm">
            Discuss, collaborate, and trade with the collective.
          </p>
        </div>
        <button
          onClick={() => { setEditingThread(null); setNewTitle(''); setNewContent(''); setNewCategory('General'); setIsModalOpen(true); }}
          className="bg-kala-secondary text-kala-900 font-bold px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors flex items-center gap-2 shadow-lg shadow-cyan-900/20"
        >
          <Plus className="w-4 h-4" /> New Topic
        </button>
      </div>

      <div className="bg-kala-800/30 border border-kala-700 rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
          <input
            type="text"
            placeholder="Search topics..."
            className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white focus:ring-1 focus:ring-kala-secondary outline-none"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-kala-700 text-white border border-kala-500'
                  : 'bg-kala-900 text-kala-400 border border-kala-700 hover:bg-kala-800'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {filteredThreads.map((thread) => (
            <ForumThreadRow
              key={thread.id}
              thread={thread}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              onEdit={handleEditThread}
              onDelete={handleDeleteThread}
              onSelect={handleSelectThread}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
          ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-4 bg-kala-800 border-b border-kala-700 flex justify-between items-center">
              <h3 className="text-white font-bold">{editingThread ? 'Edit Discussion' : 'Start New Discussion'}</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-kala-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-red-200">
                  {MODERATION_WARNING_TEXT}
                </p>
              </div>

              <div>
                <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">
                  Topic Title
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary"
                  placeholder="e.g. Need advice on..."
                />
              </div>

              <div>
                <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">
                  Category
                </label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary"
                >
                  <option>General</option>
                  <option>Collaboration</option>
                  <option>Gear Swap</option>
                  <option>Showcase</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">
                  Content
                </label>
                <textarea
                  rows={5}
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary resize-none"
                  placeholder="What's on your mind?"
                />
              </div>

              <button
                onClick={handleCreateOrUpdateTopic}
                className="w-full py-3 bg-kala-secondary text-kala-900 font-bold rounded-xl hover:bg-cyan-400 transition-colors"
              >
                {editingThread ? 'Update Topic' : 'Post Topic'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forum;